import React, { useState } from 'react';
import { FeedbackPojo } from './Api/FeedbackPojo';
import DateUtility from '../../services/DateUtils/DateUtils';
import { Language } from '../../language/English';
import './Feedback.css';
import CIcon from '@coreui/icons-react';
import { cilFile, cilCloudDownload, cilCheck, cilShare } from '@coreui/icons';
import {
    CButton,
    CModal,
    CModalHeader,
    CModalTitle,
    CModalBody,
    CModalFooter,
    CForm,
    CFormTextarea
} from '@coreui/react';
import { FeedbackService } from './Api/FeedbackService';
import Notification from '../../services/Notification/Notification';
import Logger from '../../services/Logger/Logger';

interface FeedbackCardProps {
    feedback: FeedbackPojo;
    onActionSuccess?: () => void;
    notification: Notification;
    feedbackService: FeedbackService;
    logger: Logger;
}

const FeedbackCard: React.FC<FeedbackCardProps> = ({ 
    feedback, 
    onActionSuccess, 
    notification, 
    feedbackService, 
    logger 
}) => {
    const dateUtils = new DateUtility();

    const [modalVisible, setModalVisible] = useState(false);
    const [replyMessage, setReplyMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);

    const getFilenameFromPath = (url: string) => {
        try {
            const decoded = decodeURIComponent(url);
            return decoded.substring(decoded.lastIndexOf('/') + 1);
        } catch {
            return 'document.pdf';
        }
    };

    const handleMarkAsRead = async () => {
        try {
            const result = await feedbackService.markAsRead(feedback.feedbackId);
            if (result.isSuccess) {
                notification.success(result.message || 'Feedback marked as read.');
                if (onActionSuccess) onActionSuccess();
            } else {
                notification.error(result.message || 'Failed to mark as read.');
            }
        } catch (error) {
            logger.error(error);
            notification.error(Language.SOMETHING_IS_WRONG);
        }
    };

    const handleSendReply = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorMessage('');

        const messageTrimmed = replyMessage.trim();
        if (!messageTrimmed) {
            setErrorMessage(Language.MESSAGE_CANNOT_BE_EMPTY);
            return;
        }

        const xssRegex = /<[^>]+>|javascript:|on\w+\s*=/i;
        if (xssRegex.test(messageTrimmed)) {
            setErrorMessage(Language.MESSAGE_CONTAINS_INVALID_HTML_OR_JAVASCRIPT);
            return;
        }

        setLoading(true);
        try {
            const result = await feedbackService.reply(feedback.feedbackId, messageTrimmed);
            if (result.isSuccess) {
                notification.success(result.message || 'Reply sent successfully.');
                setReplyMessage('');
                setModalVisible(false);
                if (onActionSuccess) onActionSuccess();
            } else {
                notification.error(result.message || 'Failed to send reply.');
            }
        } catch (error) {
            logger.error(error);
            notification.error(Language.SOMETHING_IS_WRONG);
        } finally {
            setLoading(false);
        }
    };

    const renderAttachment = (path: string, index: number) => {
        const isImage = /\.(jpg|jpeg|png|webp|gif)$/i.test(path);
        
        if (isImage) {
            return (
                <div key={index} className="attachment-container mb-2 me-2">
                    <a href={path} target="_blank" rel="noopener noreferrer" className="text-white text-decoration-none">
                        <img src={path} alt="attachment" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <div className="attachment-overlay">
                            <CIcon icon={cilCloudDownload} size="xl" className="text-white" />
                        </div>
                    </a>
                </div>
            );
        } else {
            return (
                <div key={index} className="attachment-container mb-2 me-2" style={{ backgroundColor: '#fff5f5', border: '1px solid #fcdcdc' }}>
                    <a href={path} target="_blank" rel="noopener noreferrer" className="text-decoration-none d-flex align-items-center justify-content-center h-100 w-100">
                        <div className="text-center">
                            <CIcon icon={cilFile} size="xl" className="text-danger" style={{ height: '32px' }} />
                            <div className="text-danger fw-bold mt-1" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>{Language.PDF || 'PDF'}</div>
                        </div>
                        <div className="attachment-overlay" style={{ background: 'rgba(220, 53, 69, 0.9)' }}>
                            <CIcon icon={cilCloudDownload} size="xl" className="text-white" />
                        </div>
                    </a>
                </div>
            );
        }
    };

    return (
        <div className="col-12 col-md-6 col-xl-4 mb-4">
            <div className="card h-100 shadow-sm border-0 rounded-4 overflow-hidden feedback-card d-flex flex-column">
                <div className="card-header p-3 border-0 d-flex justify-content-between align-items-center border-bottom" style={{ backgroundColor: '#e2e8f0' }}>
                    <span className="fw-bold text-dark">
                        {feedback.name}
                    </span>
                    <small className="text-muted fw-medium">{dateUtils.formatUnixTimestampToDateString(feedback.createdOn)}</small>
                </div>
                <div className="card-body flex-grow-1">
                    <div className="mb-3">
                        <div className="d-flex align-items-center mb-2">
                            <span className="text-dark fw-medium">{feedback.email}</span>
                        </div>
                        <div className="d-flex align-items-center mb-2">
                            <span className="text-dark fw-medium">
                                {feedback.phoneNumberCountryCode} {feedback.phoneNumber}
                            </span>
                        </div>
                    </div>
                    
                    <hr className="my-3 opacity-10" />
                    
                    <div className="mb-3">
                        <h6 className="text-muted text-uppercase small fw-bold mb-2">{Language.MESSAGE || 'Message'}</h6>
                        <div style={{ maxHeight: '180px', overflowY: 'auto', paddingRight: '8px' }}>
                            <p className="card-text text-dark" style={{ whiteSpace: 'pre-wrap', fontSize: '0.95rem', lineHeight: '1.5' }}>
                                {feedback.message}
                            </p>
                        </div>
                    </div>

                    {feedback.attachments && feedback.attachments.length > 0 && (
                        <div className="mb-3">
                            <h6 className="text-muted text-uppercase small fw-bold mb-2">{Language.ATTACHMENTS || 'Attachments'}</h6>
                            <div className="d-flex flex-wrap">
                                {feedback.attachments.map((path, idx) => renderAttachment(path, idx))}
                            </div>
                        </div>
                    )}

                    {feedback.replyMessage && (
                        <div className="mt-3 p-3 rounded-3" style={{ backgroundColor: '#f1f5f9', borderLeft: '4px solid #3b82f6' }}>
                            <div>
                                <span className="fw-bold text-primary feedback-meta-label">
                                    <CIcon icon={cilShare} className="me-1 feedback-meta-icon" style={{ transform: 'scaleX(-1)' }} />
                                    {Language.REPLIED} {feedback.replier?.name ? `${Language.BY} ${feedback.replier.name}` : ''}
                                </span>
                            </div>
                            <p className="card-text text-dark mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', lineHeight: '1.4' }}>
                                {feedback.replyMessage}
                            </p>
                            <div className="text-end mt-1">
                                <small className="text-muted feedback-meta-time">
                                    {feedback.replyOn ? dateUtils.formatUnixTimestampToDateString(feedback.replyOn) : ''}
                                </small>
                            </div>
                        </div>
                    )}

                    {feedback.markAsRead && (
                        <div className="mt-3 p-3 rounded-3" style={{ backgroundColor: '#f0fdf4', borderLeft: '4px solid #16a34a' }}>
                            <div>
                                <span className="fw-bold text-success feedback-meta-label">
                                    <CIcon icon={cilCheck} className="me-1 feedback-meta-icon" />
                                    {Language.READ_BY} {feedback.reader?.name || 'Admin'}
                                </span>
                            </div>
                            <div className="text-end mt-1">
                                <small className="text-muted feedback-meta-time">
                                    {feedback.markAsReadOn ? dateUtils.formatUnixTimestampToDateString(feedback.markAsReadOn) : ''}
                                </small>
                            </div>
                        </div>
                    )}
                </div>

                <div className="card-footer d-flex gap-2 justify-content-end align-items-center border-0 bg-transparent px-3 pb-3 pt-0">
                    {!feedback.markAsRead ? (
                        <CButton color="success" variant="outline" size="sm" className="fw-semibold px-3 rounded-pill d-flex align-items-center gap-1 feedback-btn" onClick={handleMarkAsRead}>
                            <CIcon icon={cilCheck} style={{ width: '14px', height: '14px' }} />
                            {Language.MARK_AS_READ}
                        </CButton>
                    ) : (
                        <span className="text-success fw-bold d-flex align-items-center gap-1 px-2" style={{ fontSize: '0.85rem' }}>
                            <CIcon icon={cilCheck} style={{ width: '16px', height: '16px' }} />
                            {Language.READ}
                        </span>
                    )}

                    {!feedback.replyMessage && (
                        <CButton color="primary" variant="outline" size="sm" className="fw-semibold px-3 rounded-pill d-flex align-items-center gap-1 feedback-btn" onClick={() => setModalVisible(true)}>
                            <CIcon icon={cilShare} style={{ width: '14px', height: '14px' }} />
                            {Language.REPLY}
                        </CButton>
                    )} 
                </div>
            </div>

            {/* Center Aligned Reply Modal */}
            <CModal alignment="center" visible={modalVisible} onClose={() => { setModalVisible(false); setErrorMessage(''); setReplyMessage(''); }}>
                <CModalHeader>
                    <CModalTitle className="fw-bold text-dark">{Language.REPLY_TO} {feedback.name}</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    <CForm onSubmit={handleSendReply}>
                        <div className="mb-3">
                            <label className="form-label fw-bold text-muted small text-uppercase">{Language.MESSAGE}</label>
                            <CFormTextarea
                                rows={6}
                                value={replyMessage}
                                placeholder={Language.TYPE_YOUR_REPLY_MESSAGE_HERE}
                                onChange={(e) => {
                                    setReplyMessage(e.target.value);
                                    if (errorMessage) setErrorMessage('');
                                }}
                                className={errorMessage ? 'is-invalid' : ''}
                                style={{ resize: 'none' }}
                                disabled={loading}
                            />
                            {errorMessage && (
                                <div className="invalid-feedback fw-semibold mt-1">
                                    {errorMessage}
                                </div>
                            )}
                        </div>
                    </CForm>
                </CModalBody>
                <CModalFooter className="border-0 pt-0">
                    <CButton color="secondary" variant="ghost" className="fw-semibold" onClick={() => { setModalVisible(false); setErrorMessage(''); setReplyMessage(''); }} disabled={loading}>
                        {Language.CANCEL}
                    </CButton>
                    <CButton color="primary" className="fw-semibold px-4" onClick={handleSendReply} disabled={loading}>
                        {loading ? Language.SENDING : Language.SEND}
                    </CButton>
                </CModalFooter>
            </CModal>
        </div>
    );
};

export default FeedbackCard;
